import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { UserPreference } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

const UserPreferenceQueryKeys = {
    all: ['user-preferences'] as const,
};

const WritingStyleQueryKeys = {
    all: ['writing-style'] as const,
};

const ChatMemoryQueryKeys = {
    all: ['chat-memory'] as const,
};

export const useUserPreferences = () =>
    useQuery({
        queryKey: UserPreferenceQueryKeys.all,
        queryFn: async () => {
            const [response] = await CustomerApi().listUserPreferences();
            return response ?? [];
        },
    });

export const useAutoScheduleMeetingBotPreference = () => {
    const { data: preferences, ...query } = useUserPreferences();

    const autoScheduleMeetingBot = preferences?.find(
        ({ preference }) => preference === UserPreference.AUTO_SCHEDULE_MEETING_BOT,
    );

    return {
        ...query,
        data: autoScheduleMeetingBot?.enabled ?? false,
    };
};

export const useUpdateUserPreference = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ preference, enabled }: { preference: UserPreference; enabled: boolean }) => {
            await CustomerApi().updateUserPreference(preference, { enabled });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserPreferenceQueryKeys.all });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update user preference.');
        },
    });
};

export const useWritingStyle = () =>
    useQuery({
        queryKey: WritingStyleQueryKeys.all,
        queryFn: async () => {
            const [response] = await CustomerApi().getWritingStyle();
            return response;
        },
    });

export const useUpdateWritingStyle = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (styleDescription: string) => {
            const [response] = await CustomerApi().updateWritingStyle({ styleDescription });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: WritingStyleQueryKeys.all });
            Toast.success('Writing style updated successfully');
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update writing style.');
        },
    });
};

export const useChatMemory = () =>
    useQuery({
        queryKey: ChatMemoryQueryKeys.all,
        queryFn: async () => {
            const [response] = await CustomerApi().getChatMemory();
            return response;
        },
    });

export const useUpdateChatMemory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (content: string) => {
            const [response] = await CustomerApi().updateChatMemory({ content });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ChatMemoryQueryKeys.all });
            Toast.success('Memory updated successfully');
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update memory.');
        },
    });
};

export const useDeleteChatMemory = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            await CustomerApi().deleteChatMemory();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ChatMemoryQueryKeys.all });
            Toast.success('Memory cleared');
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to clear memory.');
        },
    });
};
